import React, { useCallback, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Fade } from '@glu/transitions-react';
import { withTheme } from '@glu/theming';
import DialogWrapper from './DialogWrapper';
import Dialog from './Dialog';

export function tab(event) {
  if (!event) {
    return false;
  }

  if (event.key) {
    return event.key.toLowerCase() === 'tab';
  }

  return (event.keyIdentifier || event.keyCode) === 9;
}

const Modal = ({ className, ...props }) => {
  const {
    transition: Transition,
    theme: {
      transitions: {
        duration: { short }
      }
    },
    onHide
  } = props;

  const [transitionIn, setTransitionIn] = useState(true);

  useEffect(() => {
    if (!transitionIn) {
      const timeout = setTimeout(() => {
        setTransitionIn(true);
        onHide();
      }, short);

      return () => clearTimeout(timeout);
    }

    return undefined;
  }, [onHide, short, transitionIn]);

  const closeModal = useCallback((event) => {
    if (tab(event)) {
      return;
    }

    if (typeof Transition === 'string') {
      onHide();
      return;
    }

    setTransitionIn(false);
  }, [Transition, onHide]);
  return (
    <DialogWrapper
      className={className}
      closeModal={closeModal}
      transitionIn={transitionIn}
      {...props}
    >
      <Dialog closeModal={closeModal} {...props} />
    </DialogWrapper>
  );
};

Modal.propTypes = {
  children: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.node), PropTypes.node]),
  classes: PropTypes.objectOf(PropTypes.string),
  className: PropTypes.string,
  closeButton: PropTypes.bool,
  footer: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.element),
    PropTypes.element,
    PropTypes.string
  ]),
  header: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.element),
    PropTypes.element,
    PropTypes.string
  ]),
  hideBackdrop: PropTypes.bool,
  mountOnEnter: PropTypes.bool,
  onHide: PropTypes.func,
  show: PropTypes.bool,
  suppressIcon: PropTypes.bool,
  theme: PropTypes.objectOf(PropTypes.string),
  transition: PropTypes.oneOfType([
    PropTypes.func, PropTypes.node, PropTypes.string, PropTypes.object
  ]),
  unmountOnExit: PropTypes.bool,
  variant: PropTypes.oneOf(['info', 'success', 'error', 'warning'])
};

Modal.defaultProps = {
  children: [],
  classes: {},
  className: '',
  closeButton: true,
  footer: null,
  header: null,
  hideBackdrop: false,
  mountOnEnter: true,
  // istanbul ignore next
  onHide() {},
  show: false,
  suppressIcon: false,
  theme: {},
  transition: Fade,
  unmountOnExit: true,
  variant: null
};

const StyledModal = withTheme(Modal);
StyledModal.displayName = 'Modal';

export default StyledModal;
